import { AfterViewInit, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FieldSelectedValue, FormFieldConfig, SECTION_TYPE, TabConfig } from "../../model/form.model";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { EventBillingTitle, EventBillingTabSchema, EventBillingFilterInfoText, FetchConfig, SectionNames } from "../../model/event-billing.model";
import { DYNAMIC_IDENTIFIER, LAYOUT_TYPE } from "../../model/common.model";
import { FieldNames, FieldTypesForOptions } from "../../model/form-fields.model";
import { CommonService } from "../../services/common.service";
import { Subject } from "rxjs";
import { ApiEnvNames, API_STATUS } from "../../model/api.model";
type EventData = { eventId: number; eventName: string; year: string };
@Component({
  selector: 'app-event-billing',
  standalone: true,
  providers: [],
  imports: [PageContainerComponent],
  templateUrl: './event-billing.component.html',
  styleUrl: './event-billing.component.scss',
})
export class EventBillingComponent implements OnInit, AfterViewInit, OnDestroy {
  title = EventBillingTitle;
  readonly LayoutType = LAYOUT_TYPE;
  tabConfig: Array<TabConfig> = EventBillingTabSchema;
  globalChartsPayload = new Subject<any>();
  globalFilterFields: FormFieldConfig[] = [];
  globalTitleFields = { [DYNAMIC_IDENTIFIER['globalyear']]: '' };
  private readonly commonService = inject(CommonService);
  private selectedEventId: number | string = '';
  private selectedYear = '';
  private eventsCache: EventData[] | null = null;
  private isDashboardActive = true;
  private dashboardInitialized = false;

  ngOnInit(): void {
    this.resetState();
    this.clearDashboard();
    this.initializeTab();
  }

  ngAfterViewInit(): void {
    void this.initDashboard();
  }

  ngOnDestroy(): void {
    this.clearDashboard();
    this.resetState();
  }

  private initializeTab(): void {
    this.tabConfig.forEach(tab => {
      tab['tabOnChangeSubject'] = new Subject();
      tab['tabOnChangeSubject'].subscribe(() => {
        this.isDashboardActive = tab['type'] === SECTION_TYPE['Dashboard'];
        if (this.isDashboardActive) {
          this.bindTabInputs(tab);
          setTimeout(() => {
            if (this.isDashboardActive && this.hasRequiredFilters()) {
              this.emitChartPayload(FieldNames['Event']);
            }
          });
        }
      });
      this.bindTabInputs(tab);
    });
  }

  private bindTabInputs(tab: TabConfig): void {
    if (tab['type'] !== SECTION_TYPE['Dashboard']){ return };
    tab['inputs']['gridSchema']?.forEach((gridElm: any) => {
      if (!gridElm?.inputs){ return };
      if (gridElm['type'] === SECTION_TYPE['GlobalFilter'] && gridElm.inputs?.dynamicFormArray) {
        gridElm.inputs.dynamicFormArray.onButtonClicked = () => {};
        gridElm.inputs.dynamicFormArray.onControlValueChanged = this.onFilterChange;
        if (gridElm.inputs.dynamicFormArray?.FieldsArray) {
          this.globalFilterFields = gridElm.inputs.dynamicFormArray.FieldsArray;
        }
      }
      if (gridElm['type'] === SECTION_TYPE['Chart']) {
        gridElm.inputs.filterSchema ??= {};
        gridElm.inputs.filterSchema['globalPayloadSubject'] = this.globalChartsPayload;
        gridElm.inputs.filterSchema['globalTitleFields'] = this.globalTitleFields;
      }
    });
  }

  private async initDashboard(): Promise<void> {
    if (!this.isDashboardActive) {return};
    this.resolveFilterFields();
    if (!this.globalFilterFields.length) {return};
    try {
      await this.loadDropdownOptions(this.globalFilterFields);
    } catch (e) {
      console.info(e); 
    }
    if (!this.isDashboardActive) {return};
    const yearField = this.globalFilterFields.find(f => f['fieldName'] === FieldNames['Year']);
    const eventField = this.globalFilterFields.find(f => f['fieldName'] === FieldNames['Event']);
    this.selectedYear = yearField?.['selectedValue'] ?? '';
    this.selectedEventId = eventField?.['selectedValue'] ?? '';
    this.globalTitleFields[DYNAMIC_IDENTIFIER['globalyear']] = this.selectedYear;
    if (this.hasRequiredFilters() && !this.dashboardInitialized) {
      this.dashboardInitialized = true;
      this.triggerSummaryCards();
      this.emitChartPayload(FieldNames['Year']);
    }
    this.refreshInfoText();
  }

  private resolveFilterFields(): void {
    if (this.globalFilterFields.length > 0) {return};
    const dashboardTab = this.tabConfig.find(t => t['type'] === SECTION_TYPE['Dashboard']);
    const filterSection = dashboardTab?.['inputs']?.['gridSchema']?.find((g: any) => g?.type === SECTION_TYPE['GlobalFilter']);
    const fields = filterSection?.inputs?.dynamicFormArray?.FieldsArray;
    if (Array.isArray(fields)) {
      this.globalFilterFields = fields;
    }
  }

  onFilterChange = (event: FieldSelectedValue): void => {
    if (!this.isDashboardActive) {return};
    const changed = event?.FieldArray?.find(f => f['fieldName'] === event['FieldName']);
    if (!changed) {return};
    if (changed['fieldName'] === FieldNames['Year']) {
      this.selectedYear = changed['selectedValue'];
      this.globalTitleFields[DYNAMIC_IDENTIFIER['globalyear']] = this.selectedYear;
      this.updateEventOptions(this.selectedYear);
    } else if (changed['fieldName'] === FieldNames['Event']) {
      this.selectedEventId = changed['selectedValue'];
    }
    this.clearChartsData();
    if (this.hasRequiredFilters()) {
      this.triggerSummaryCards();
      this.emitChartPayload(changed['fieldName']);
    } else {
      this.clearSummaryCards();
    }
    this.refreshInfoText();
  };

  private updateEventOptions(year: string): void {
    const eventField = this.globalFilterFields.find(f => f['fieldName'] === FieldNames['Event']);
    if (!eventField || !this.eventsCache) {return};
    const filtered = year ? this.eventsCache.filter(e => String(e.year) === String(year)): this.eventsCache;
    eventField['options'] = filtered.map(e => ({ fieldLabel: e.eventName, fieldValue: String(e.eventId) }));
    const isCurrentEventValid = filtered.some(e => String(e.eventId) === String(eventField['selectedValue']));
    if (!isCurrentEventValid) {
      eventField['selectedValue'] = '';
      this.selectedEventId = '';
    }
  }

  private emitChartPayload(fieldName: string): void {
    if (!this.isDashboardActive) {return};
    this.globalChartsPayload.next({
      FieldName: fieldName,
      Value: {
        body: { eventId: this.selectedEventId },
        title: structuredClone(this.globalTitleFields)
      }
    });
  }

  private clearChartsData(): void {
    const dashboardTab = this.tabConfig.find(t => t['type'] === SECTION_TYPE['Dashboard']);
    dashboardTab?.['inputs']?.['gridSchema']?.forEach((gridElm: any) => {
      if (gridElm?.type !== SECTION_TYPE['Chart'] || !gridElm?.inputs?.chartSchema) {return};
      const chart = gridElm.inputs.chartSchema;
      chart?.series?.forEach((s: any) => { s.data = []; });
      if (chart?.drilldown?.series){ chart.drilldown.series = []};
      if (chart?.xAxis?.categories){ chart.xAxis.categories = []};
    });
  }

  private triggerSummaryCards(): void {
    if (!this.hasRequiredFilters()) {return};
    this.setSummaryApiConfig({ eventId: this.selectedEventId }, `${this.selectedEventId}|${Date.now()}`);
  }

  private clearSummaryCards(): void {
    this.setSummaryApiConfig({ eventId: '' }, '');
  }

  private setSummaryApiConfig(body: object, groupType: string): void {
    this.tabConfig.forEach(tab => {
      if (tab['type'] !== SECTION_TYPE['Dashboard']) {return};
      tab['inputs']?.['gridSchema']?.forEach((gridElm: any) => {
        if (gridElm?.inputs?.elementId !== SectionNames['EventBillingSummary']) {return};
        gridElm.inputs.apiConfig = { ...gridElm.inputs.apiConfig, body };
        gridElm.inputs.groupType = groupType;
      });
    });
  }

  private refreshInfoText(): void {
    const dashboardTab = this.tabConfig.find(t => t['type'] === SECTION_TYPE['Dashboard']);
    if (dashboardTab?.['inputs']) {
      dashboardTab['inputs']['props'] = {
        ...dashboardTab['inputs']['props'],
        title: this.hasRequiredFilters() ? '' : EventBillingFilterInfoText
      };
    }
  }

  async loadDropdownOptions(fieldArray: FormFieldConfig[]): Promise<void> {
    const hasYearOrEvent = fieldArray.some(f =>
      f['fieldName'] === FieldNames['Event'] || f['fieldName'] === FieldNames['Year']
    );
    if (hasYearOrEvent && !this.eventsCache) {
      this.eventsCache = await this.fetchEvents();
    }
    const allEvents = this.eventsCache ?? [];
    const yearField = fieldArray.find(f => f['fieldName'] === FieldNames['Year']);
    const eventField = fieldArray.find(f => f['fieldName'] === FieldNames['Event']);
    if (yearField && FieldTypesForOptions.includes(yearField['fieldType'])) {
      const uniqueYears = [...new Set(allEvents.map(e => String(e.year)))];
      yearField['options'] = uniqueYears.map(y => ({ fieldLabel: y, fieldValue: y }));
      yearField['isLoading'] = false;
    }
    if (eventField && FieldTypesForOptions.includes(eventField['fieldType'])) {
      const selectedYear = yearField?.['selectedValue'] ?? '';
      const filtered = selectedYear ? allEvents.filter(e => String(e.year) === String(selectedYear)): allEvents;
      eventField['options'] = filtered.map(e => ({ fieldLabel: e.eventName, fieldValue: String(e.eventId) }));
      eventField['isLoading'] = false;
    }
  }

  private async fetchEvents(): Promise<EventData[]> {
    try {
      const config = FetchConfig[SectionNames['GetEventsAndYears']];
      const response = await this.commonService.getDataFromDataSource(config, ApiEnvNames['EventBilling']);
      if (response?.status === API_STATUS.Success) {
        const result = response.result;
        if (Array.isArray(result)) {return result as EventData[]};
        const inner = (result as any[])?.[0]?.result;
        if (Array.isArray(inner)){ return inner as EventData[]};
      }
    } catch (err) {
      console.info('Event Billing - fetchEvents error:', err);
    }
    return [];
  }

  private resetState(): void {
    this.selectedEventId = '';
    this.selectedYear = '';
    this.globalTitleFields[DYNAMIC_IDENTIFIER['globalyear']] = '';
    this.globalFilterFields = [];
    this.globalChartsPayload = new Subject<any>();
    this.eventsCache = null;
    this.dashboardInitialized = false;
  }

  private clearDashboard(): void {
    this.tabConfig.forEach(tab => {
      if (tab['type'] === SECTION_TYPE['Dashboard']) {
        this.clearDashboardState(tab);
      }
    });
  }

  private clearDashboardState(tab: TabConfig): void {
    tab['inputs']?.['gridSchema']?.forEach((gridElm: any) => {
      const { type: sectionType, inputs } = gridElm ?? {};
      if (!inputs) {return};
      if (sectionType === SECTION_TYPE['GlobalFilter']) {
        (inputs?.dynamicFormArray?.FieldsArray ?? []).forEach((field: FormFieldConfig) => {
          if (field['fieldName'] === FieldNames['Year'] || field['fieldName'] === FieldNames['Event']) {
            field['selectedValue'] = '';
            field['defaultValue'] = '';
            field['isDefaultSet'] = false;
          }
        });
      }
      if (inputs?.elementId === SectionNames['EventBillingSummary']) {
        inputs.apiConfig = { ...inputs.apiConfig, body: { eventId: '' } };
        inputs.groupType = '';
      }
      if (sectionType === SECTION_TYPE['Chart'] && inputs?.chartSchema) {
        const chart = inputs.chartSchema;
        chart?.series?.forEach((s: any) => { s.data = []; });
        if (chart?.drilldown?.series){ chart.drilldown.series = []};
        if (chart?.xAxis?.categories){ chart.xAxis.categories = []};
      }
    });
  }

  private hasRequiredFilters(): boolean {
    return !!this.selectedEventId && !!this.selectedYear;
  }
}
